// src/components/dashboard/StatsCard.js
import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Clock, 
  Brain, 
  BarChart3,
  ArrowUp,
  ArrowDown,
  Calendar,
  Flame
} from 'lucide-react';
import './StatsCard.css';

const StatsCard = ({ stats = {}, user = {} }) => {
  // Prefer flashcard-stats from dashboard if present
  const fc = stats?.flashcardStats || {};

  const statsMap = {
    total: {
      title: 'Total Flashcards',
      value: fc.totalCards ?? stats?.flashcardsCount ?? user?.flashcardsCount ?? 0,
      change: null,
      trend: 'up',
      icon: <Brain size={24} />,
      color: '#3b82f6'
    },
    mastered: {
      title: 'Mastered',
      value: fc.masteredCards ?? 0,
      change: null,
      trend: 'up',
      icon: <Target size={24} />,
      color: '#10b981'
    },
    due: {
      title: 'Due Today',
      value: fc.dueForReview ?? 0,
      change: null,
      trend: (fc.dueForReview ?? 0) > 0 ? 'down' : 'up',
      icon: <Clock size={24} />,
      color: '#f59e0b'
    },
    streak: {
      title: 'Study Streak',
      value: user?.studyStreak ? `${user.studyStreak} days` : '—',
      change: null,
      trend: 'up',
      icon: <Flame size={24} />,
      color: '#ec4899'
    },
    accuracy: {
      title: 'Mastery %',
      value: `${fc.masteryPercentage ?? 0}%`,
      change: null,
      trend: 'up',
      icon: <BarChart3 size={24} />,
      color: '#8b5cf6'
    },
    time: {
      title: 'Study Time',
      value: user?.studyTime ? `${user.studyTime}h` : '—',
      change: null,
      trend: 'up',
      icon: <Calendar size={24} />,
      color: '#06b6d4'
    }
  };

  // Remove placeholder progress data - will be replaced with real data later if needed
  const progressData = [];

  // Dynamic recent activity based on real data
  const recentActivity = [
    { action: 'Reviewed flashcards', time: '2 hours ago', count: fc.dueForReview ?? 0 },
    { action: 'Created new deck', time: 'Yesterday', count: 1 },
    { action: 'Achieved mastery', time: '2 days ago', count: fc.masteredCards ?? 0 },
    { action: 'Generated with AI', time: '3 days ago', count: 15 }
  ];

  return (
    <div className="stats-card">
      <div className="stats-header">
        <h2>Study Analytics</h2>
        <div className="date-range">
          <span>Last 30 days</span>
          <TrendingUp size={16} />
        </div>
      </div>

      <div className="stats-grid">
        {Object.entries(statsMap).map(([key, stat]) => (
          <div key={key} className="stat-item">
            <div className="stat-icon" style={{ backgroundColor: `${stat.color}20` }}>
              <span style={{ color: stat.color }}>
                {stat.icon}
              </span>
            </div>
            <div className="stat-content">
              <div className="stat-title">{stat.title}</div>
              <div className="stat-value">{stat.value}</div>
              <div className={`stat-change ${stat.trend}`}>
                {stat.trend === 'up' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {stat.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="stats-sections">
        <div className="progress-section">
          <h3>Subject Progress</h3>
          <div className="progress-bars">
            {progressData.map((item, index) => (
              <div key={index} className="progress-item">
                <div className="progress-header">
                  <span className="progress-label">{item.subject}</span>
                  <span className="progress-percent">{item.progress}%</span>
                </div>
                <div className="progress-track">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${item.progress}%`,
                      backgroundColor: item.color
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="activity-section">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {activity.action.includes('Reviewed') && '📚'}
                  {activity.action.includes('Created') && '📁'}
                  {activity.action.includes('mastery') && '🎯'}
                  {activity.action.includes('AI') && '🤖'}
                </div>
                <div className="activity-content">
                  <div className="activity-action">{activity.action}</div>
                  <div className="activity-meta">
                    <span className="activity-time">{activity.time}</span>
                    {activity.count > 1 && (
                      <span className="activity-count">• {activity.count} items</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="stats-tips">
        <div className="tip-card">
          <div className="tip-icon">💡</div>
          <div className="tip-content">
            <h4>Study Tip</h4>
            <p>Review due cards daily to maintain your streak!</p>
          </div>
        </div>
        <div className="tip-card">
          <div className="tip-icon">🚀</div>
          <div className="tip-content">
            <h4>Quick Action</h4>
            <p>You have {fc?.dueForReview ?? 0} cards due for review. Start a study session now!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsCard;